import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  Table,
  Modal,
  Dialog,
  Button,
  Field,
  Tabs,
  Loader,
  Spinner,
  Select,
  Flex,
} from '@oliasoft-open-source/react-ui-library';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { getResolver } from 'src/validation/resolver';
import { Input, NumberInput } from 'client/common/form-inputs';
import { getProjectSettings, saveProjectSettings } from 'store/entities/project-settings/project-settings';
import { projectNameUpdated } from 'store/entities/projects/projects';
import { projectSettingsSchemaValidator } from 'schemas/ajv-validators';
import translations from 'src/internationalisation/translation-map.json';
import { CurrencyExchangeDeleteModal } from 'src/common/components/currency-tab/currency-exchange-delete.modal';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { FeatureFlagName } from 'src/enums/feature-flags';

const ProjectSettingsModal = ({
  settings,
  setModalVisible,
  getProjectSettings,
  saveProjectSettings,
  projectNameUpdated,
  additionalCurrencyOptions,
  isFetching,
  isAdding,
  currencyTemplate,
  currencyOptions,
}) => {
  const { t } = useTranslation();
  const { project } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [additionalCurrenciesList, setAdditionalCurrenciesList] = useState([]);
  const [primaryCurrency, setPrimaryCurrency] = useState('');
  const [selectedCurrencyTemplateId, setSelectedCurrencyTemplateId] = useState(
    currencyTemplate.find((item) => item.isDefault)?.currencyTemplateId || null,
  );
  const [isRemoveCurrencyModalVisible, setIsRemoveCurrencyModalVisible] = useState(false);
  const [isApplyCurrencyTemplateModalVisible, setIsAplyCurrencyTemplateModalVisible] = useState(false);
  const [selectedIndexToRemove, setSelectedIndexToRemove] = useState(null);

  const isProjectCurrencySettingsActive = FeatureFlags.isActive(FeatureFlagName.UserSetCurrency);

  const currencyTemplateOptions = currencyTemplate
    ? [...currencyTemplate]
        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
        .map((item) => ({
          label: item.name,
          value: item.currencyTemplateId,
        }))
    : [];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      projectName: settings.projectName,
      threadsPerSimulation: settings.threadsPerSimulation,
      primaryCurrency: settings.primaryCurrency,
    },
    resolver: getResolver(projectSettingsSchemaValidator),
    mode: 'onChange',
  });

  const mappedCurrencyOptions = React.useMemo(() => {
    const primaryCurrency = getValues('primaryCurrency');
    return currencyOptions.map((c) => ({
      ...c,
      disabled: additionalCurrenciesList.some((cur) => cur.currency === c.value || primaryCurrency === c.value),
    }));
  }, [additionalCurrenciesList, currencyOptions, getValues]);

  useEffect(() => {
    getProjectSettings(project);
  }, []);

  useEffect(() => {
    const { projectName, threadsPerSimulation, primaryCurrency, additionalCurrencies } = settings;
    setAdditionalCurrenciesList(additionalCurrencies || []);
    setPrimaryCurrency(primaryCurrency);
    reset({ projectName, threadsPerSimulation });
  }, [settings]);

  const onClose = () => setModalVisible(false);
  const onSubmit = handleSubmit((data) => {
    projectNameUpdated(data.projectName);
    saveProjectSettings(project, {
      ...data,
      additionalCurrencies: additionalCurrenciesList,
      primaryCurrency,
    });
  });

  const onAddCurrency = () =>
    setAdditionalCurrenciesList([
      ...additionalCurrenciesList,
      {
        currency: mappedCurrencyOptions.filter((o) => !o.disabled)[0]?.value,
        exchangeRate: 100,
      },
    ]);

  const onChangeAdditionalCurrency = (ev, i) => {
    const newElement = [...additionalCurrenciesList];
    if (ev.target.name === 'currency') {
      newElement[i] = {
        ...newElement[i],
        currency: ev.target.value,
      };
    } else if (ev.target.name === 'exchangeRate') {
      newElement[i] = {
        ...newElement[i],
        exchangeRate: ev.target.value,
      };
    }
    setAdditionalCurrenciesList(newElement);
  };

  const tabsList = [
    {
      label: t(translations.projectSettings_monteCarloSimulation),
      value: 0,
    },
  ];

  const handleConfirmRemove = () => {
    if (selectedIndexToRemove !== null) {
      const newCurrencies = [...additionalCurrenciesList];
      newCurrencies.splice(selectedIndexToRemove, 1);
      setAdditionalCurrenciesList(newCurrencies);

      setIsRemoveCurrencyModalVisible(false);
      setSelectedIndexToRemove(null);
    }
  };

  const handleConfirmApplyTemplate = () => {
    const selectedTemplate = currencyTemplate.find((item) => item.currencyTemplateId === selectedCurrencyTemplateId);
    if (selectedTemplate) {
      setAdditionalCurrenciesList(selectedTemplate.additionalCurrencies);
    }
    setIsAplyCurrencyTemplateModalVisible(false);
  };

  return (
    <>
      <Modal visible centered>
        <Dialog
          dialog={{
            width: '500px',
            heading: t(translations.projects_projectSettings),
            content: (
              <div
                style={{
                  position: 'relative',
                  margin: 'calc(var(--padding) * -1)',
                }}
              >
                {isFetching && (
                  <Loader text={t(translations.adding)} theme="light" cover>
                    <Spinner dark />
                  </Loader>
                )}
                <form style={{ padding: 'var(--padding)' }}>
                  <Field label={t(translations.projects_projectName)}>
                    <Input name="projectName" control={control} errors={errors} />
                  </Field>
                  <Tabs
                    onChange={(ev) => {
                      setActiveTab(ev.target.value);
                    }}
                    value={activeTab}
                    options={
                      isProjectCurrencySettingsActive
                        ? [...tabsList, { label: t(translations.currency), value: 1 }]
                        : tabsList
                    }
                  />
                  {activeTab === 0 ? (
                    <Field label={t(translations.projectSettings_threadsPerSimulation)}>
                      <NumberInput name="threadsPerSimulation" width="135px" control={control} errors={errors} />
                    </Field>
                  ) : (
                    <>
                      <Field label={t(translations.currency_template)}>
                        <Flex gap alignItems="center">
                          <Select
                            name={'currencyTemplate'}
                            options={currencyTemplateOptions}
                            value={selectedCurrencyTemplateId}
                            onChange={(e) => setSelectedCurrencyTemplateId(e.target.value)}
                            width="200px"
                            small
                          />
                          <Button
                            label={t(translations.apply)}
                            onClick={() => {
                              setIsAplyCurrencyTemplateModalVisible(true);
                            }}
                            colored
                            small
                          />
                        </Flex>
                      </Field>

                      <Field label={t(translations.projectSettings_primaryCurrency)}>
                        <Select
                          name="primaryCurrency"
                          options={mappedCurrencyOptions}
                          value={primaryCurrency}
                          onChange={(e) => {
                            setPrimaryCurrency(e.target.value);
                          }}
                          disabled
                        />
                      </Field>
                      <Table
                        table={{
                          fixedWidth: '100%',
                          columnAlignment: ['left', 'right', 'left'],
                          headers: [
                            {
                              cells: [
                                {
                                  value: t(translations.projectSettings_additionalCurrency),
                                },
                                {
                                  value: t(translations.projectSettings_exchangeRate),
                                  style: { width: '120px' },
                                },
                                { value: '', style: { width: '90px' } },
                              ],
                              actions: [
                                {
                                  primary: true,
                                  label: t(translations.add),
                                  icon: 'add',
                                  disabled: additionalCurrenciesList.length >= 10,
                                  onClick: () => {
                                    onAddCurrency();
                                  },
                                },
                              ],
                            },
                          ],
                          rows: additionalCurrenciesList.map((element, i) => ({
                            cells: [
                              {
                                name: 'currency',
                                type: 'Select',
                                options: additionalCurrencyOptions,
                                value: element.currency,
                                onChange: (ev) => {
                                  onChangeAdditionalCurrency(ev, i);
                                },
                                autoLayerWidth: true,
                              },
                              {
                                name: 'exchangeRate',
                                type: 'Input',
                                value: element.exchangeRate,
                                onChange: (ev) => {
                                  onChangeAdditionalCurrency(ev, i);
                                },
                              },
                              {
                                value: `= 1 USD`,
                                disabled: true,
                                style: {
                                  opacity: '0.5',
                                  cursor: 'default',
                                },
                              },
                            ],
                            actions: [
                              {
                                label: 'Delete',
                                icon: 'minus',
                                onClick: () => {
                                  setSelectedIndexToRemove(i);
                                  setIsRemoveCurrencyModalVisible(true);
                                },
                              },
                            ],
                          })),
                        }}
                      />
                    </>
                  )}
                </form>
              </div>
            ),
            footer: (
              <>
                <Button label={t(translations.save)} onClick={onSubmit} disabled={isAdding} colored />
                <Button label={t(translations.cancel)} onClick={onClose} />
              </>
            ),
            onClose,
          }}
        />
      </Modal>
      {isRemoveCurrencyModalVisible && (
        <CurrencyExchangeDeleteModal
          onSubmit={handleConfirmRemove}
          onClose={() => {
            setIsRemoveCurrencyModalVisible(false);
            setSelectedIndexToRemove(null);
          }}
          title={t(translations.delete_currency_exchange_rate_title)}
          message={t(translations.delete_currency_exchange_confirmation)}
          submitButtonName={t(translations.delete)}
        />
      )}
      {isApplyCurrencyTemplateModalVisible && (
        <CurrencyExchangeDeleteModal
          onSubmit={handleConfirmApplyTemplate}
          onClose={() => {
            setIsAplyCurrencyTemplateModalVisible(false);
          }}
          title={t(translations.apply_currency_template_title)}
          message={t(translations.apply_currency_template_confirmation)}
          submitButtonName={t(translations.apply)}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const {
    projectSettings: { currencies, settings, isAdding, isFetching },
  } = entities;
  const { currencyTemplate } = entities.companySettings;

  const currencyOptions = currencies.map((item) => ({
    label: `(${item.code}) ${item.name}`,
    value: item.code,
  }));

  const additionalCurrencyOptions = currencies
    .filter((item) => item.code !== 'USD')
    .map((item) => ({
      label: `(${item.code}) ${item.name}`,
      value: item.code,
    }));

  return {
    isFetching,
    isAdding,
    currencies,
    settings,
    additionalCurrencyOptions,
    currencyTemplate,
    currencyOptions,
  };
};

const mapDispatchToProps = {
  getProjectSettings,
  saveProjectSettings,
  projectNameUpdated,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(ProjectSettingsModal), {
  isModal: true,
});

export { Container as ProjectSettingsModal };
